import type { Notification } from "@src/packages/care/useNotifications";

export const BADGE_UNLOCKED_EVENT = "badge_unlocked" as const;

export type BadgeCelebrationMode = "default" | "quiet";

export type BadgeUnlockEvent = {
  eventId: string;
  badgeId: string;
  criteriaVersion: string;
  userId: string;
  sourceDomainEvent: string;
  occurredAt: string;
  copy: {
    title: string;
    body: string;
    milestone: string;
    celebrationMode: BadgeCelebrationMode;
  };
};

export type BadgeUnlockEventEnvelope = {
  notification: Notification;
  event: BadgeUnlockEvent;
};

/**
 * Badge unlocks are write-once milestones. If a backend replay contains two
 * distinct events for the same badge, retain the first occurrence so the
 * Passport date cannot move forward when history is re-read.
 */
export function preferEarlierBadgeUnlockEvent(
  current: BadgeUnlockEvent,
  candidate: BadgeUnlockEvent,
): BadgeUnlockEvent {
  const currentTime = Date.parse(current.occurredAt);
  const candidateTime = Date.parse(candidate.occurredAt);

  if (candidateTime < currentTime) return candidate;
  if (candidateTime > currentTime) return current;
  return candidate.eventId < current.eventId ? candidate : current;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readString(record: Record<string, unknown>, key: string) {
  const value = record[key];
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

function readCelebrationMode(
  record: Record<string, unknown>,
): BadgeCelebrationMode {
  return record.celebrationMode === "quiet" ? "quiet" : "default";
}

/**
 * Parses the server notification payload for the badge event contract.
 *
 * Notifications are user-scoped by the API, but the user id is still checked
 * here so a malformed or misrouted event can never become a celebration.
 */
export function parseBadgeUnlockEvent(
  notification: Notification,
): BadgeUnlockEventEnvelope | null {
  if (
    notification.type !== BADGE_UNLOCKED_EVENT ||
    !isRecord(notification.data)
  ) {
    return null;
  }

  const data = isRecord(notification.data.event)
    ? notification.data.event
    : notification.data;
  const copy = isRecord(data.copy) ? data.copy : null;
  if (!copy) return null;

  const eventId = readString(data, "eventId");
  const badgeId = readString(data, "badgeId");
  const criteriaVersion = readString(data, "criteriaVersion");
  const userId = readString(data, "userId");
  const sourceDomainEvent = readString(data, "sourceDomainEvent");
  const occurredAt = readString(data, "occurredAt");
  const title = readString(copy, "title");
  const body = readString(copy, "body");
  const milestone = readString(copy, "milestone");

  if (
    !eventId ||
    !badgeId ||
    !criteriaVersion ||
    !userId ||
    !sourceDomainEvent ||
    !occurredAt ||
    Number.isNaN(Date.parse(occurredAt)) ||
    !title ||
    !body ||
    !milestone ||
    userId !== notification.userId
  ) {
    return null;
  }

  return {
    notification,
    event: {
      eventId,
      badgeId,
      criteriaVersion,
      userId,
      sourceDomainEvent,
      occurredAt,
      copy: {
        title,
        body,
        milestone,
        celebrationMode: readCelebrationMode(copy),
      },
    },
  };
}

export function isBadgeUnlockNotification(notification: Notification): boolean {
  return parseBadgeUnlockEvent(notification) !== null;
}

/** A typed badge row that fails validation is a contract failure, not no data. */
export function isMalformedBadgeUnlockNotification(
  notification: Notification,
): boolean {
  return (
    notification.type === BADGE_UNLOCKED_EVENT &&
    parseBadgeUnlockEvent(notification) === null
  );
}
